import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  UsersIcon,
  CurrencyDollarIcon,
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  FolderIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const DashboardLayout = ({ children }) => {
  const { user, logout, getToken } = useAuth();
  const { hasPermission, hasModule } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1280);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({});
  const notifPanelRef = useRef(null);
  const notifBtnRef = useRef(null);
  // Top-bar search - jumps to a dashboard section (Settings, Orders, etc.) rather than
  // searching data records, since each section already has its own dedicated search
  // (e.g. Products has "Search products by name or description..."). This just filters
  // the same menuItems the sidebar already renders.
  const [navSearchQuery, setNavSearchQuery] = useState('');
  const [navSearchOpen, setNavSearchOpen] = useState(false);
  const navSearchRef = useRef(null);

  const groupedAlerts = useMemo(() => {
    const groups = new Map();

    const getGroupMeta = (alert) => {
      const title = (alert.title || '').toLowerCase();
      const category = (alert.category || '').toLowerCase();

      if (title.includes('out of stock')) {
        return {
          key: 'inventory_out_of_stock',
          title: 'Out of Stock',
          section: 'Inventory'
        };
      }

      if (title.includes('low stock')) {
        return {
          key: 'inventory_low_stock',
          title: 'Low Stock',
          section: 'Inventory'
        };
      }

      if (title.includes('new order') || category === 'orders') {
        return {
          key: title.includes('new order') ? 'orders_new' : `orders_${alert.title || 'general'}`,
          title: alert.title || 'Order Alerts',
          section: 'Orders'
        };
      }

      if (category === 'customers') {
        return {
          key: 'customers_new',
          title: alert.title || 'Customer Alerts',
          section: 'Customers'
        };
      }

      return {
        key: `${category || 'general'}_${alert.title || alert.type || 'alert'}`,
        title: alert.title || 'Alert',
        section: 'General'
      };
    };

    alerts.forEach((alert) => {
      const meta = getGroupMeta(alert);
      const group = groups.get(meta.key) || {
        key: meta.key,
        title: meta.title,
        section: meta.section,
        type: alert.type,
        priority: alert.priority || 'low',
        link: alert.link,
        items: [],
        count: 0,
        latestDate: alert.created_at ? new Date(alert.created_at) : new Date(0)
      };

      group.items.push(alert);
      group.count += 1;
      if (alert.link && !group.link) group.link = alert.link;
      if (alert.priority === 'high') group.priority = 'high';
      if (alert.priority === 'medium' && group.priority === 'low') group.priority = 'medium';

      const alertDate = alert.created_at ? new Date(alert.created_at) : null;
      if (alertDate && alertDate > group.latestDate) {
        group.latestDate = alertDate;
      }

      groups.set(meta.key, group);
    });

    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return Array.from(groups.values()).sort((a, b) => {
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return b.latestDate - a.latestDate;
    });
  }, [alerts]);

  const sectionCounts = useMemo(() => {
    return groupedAlerts.reduce((acc, group) => {
      acc[group.section] = (acc[group.section] || 0) + group.count;
      return acc;
    }, {});
  }, [groupedAlerts]);

  const fetchAlerts = useCallback(async () => {
    try {
      const token = getToken(true);
      if (!token) return;
      const res = await fetch('/api/dashboard/alerts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setAlerts(data.data?.alerts || []);
          setAlertCount(data.data?.unread_count || 0);
        }
      }
    } catch (e) { /* ignore network errors */ }
  }, [getToken]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const handleClickOutside = (e) => {
      if (
        notifPanelRef.current && !notifPanelRef.current.contains(e.target) &&
        notifBtnRef.current && !notifBtnRef.current.contains(e.target)
      ) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!navSearchOpen) return;
    const handleClickOutside = (e) => {
      if (navSearchRef.current && !navSearchRef.current.contains(e.target)) {
        setNavSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [navSearchOpen]);

  // Close the dropdown (but keep the typed text) whenever navigation happens, so it
  // doesn't stay open floating over the newly-loaded page.
  useEffect(() => {
    setNavSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 1280px)');
    const handleBreakpointChange = (event) => {
      setSidebarOpen(event.matches);
    };

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', handleBreakpointChange);
    } else {
      desktopQuery.addListener(handleBreakpointChange);
    }

    return () => {
      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener('change', handleBreakpointChange);
      } else {
        desktopQuery.removeListener(handleBreakpointChange);
      }
    };
  }, []);

  const handleLogout = async () => {
    logout(true); // Pass true to indicate dashboard logout
    navigate('/dashboard/login');
  };

  const getGroupSummary = (group) => {
    const lowerTitle = group.title.toLowerCase();
    if (lowerTitle.includes('out of stock')) {
      return `${group.count} product(s) currently out of stock`;
    }
    if (lowerTitle.includes('low stock')) {
      return `${group.count} product(s) running low`;
    }
    if (lowerTitle.includes('new order')) {
      return `${group.count} new order(s) need attention`;
    }
    return `${group.count} alert(s)`;
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const menuItems = [
    {
      name: 'Dashboard',
      icon: ChartBarIcon,
      path: '/dashboard',
      roles: ['admin', 'employee'],
      permission: 'dashboard.view', // Can be view, view_limited, or view_finance
      fallbackPermission: 'dashboard.view_limited' // Fallback for limited access
    },
    {
      name: 'Analytics',
      icon: ChartBarIcon,
      path: '/dashboard/analytics',
      roles: ['admin', 'employee'],
      permission: 'analytics.view'
    },
    {
      name: 'Sales',
      icon: CurrencyDollarIcon,
      path: '/dashboard/sales',
      roles: ['admin', 'employee'],
      permission: 'sales.view'
    },
    {
      name: 'Products',
      icon: ShoppingBagIcon,
      path: '/dashboard/products',
      roles: ['admin', 'employee'],
      permission: 'products.view',
      module: 'products' // For module-based checking
    },
    {
      name: 'Categories',
      icon: FolderIcon,
      path: '/dashboard/categories',
      roles: ['admin', 'employee'],
      permission: 'categories.view',
      module: 'categories'
    },
    {
      name: 'Orders',
      icon: ShoppingBagIcon,
      path: '/dashboard/orders',
      roles: ['admin', 'employee'],
      permission: 'orders.view',
      fallbackPermission: 'orders.view_assigned', // For picker/packer/delivery
      module: 'orders'
    },
    {
      name: 'Customers',
      icon: UsersIcon,
      path: '/dashboard/customers',
      roles: ['admin', 'employee'],
      permission: 'customers.view',
      module: 'customers'
    },
    {
      name: 'Employees',
      icon: UsersIcon,
      path: '/dashboard/employees',
      roles: ['admin'],
      permission: 'employees.view',
      module: 'employees'
    },
    {
      name: 'Suppliers',
      icon: TruckIcon,
      path: '/dashboard/suppliers',
      roles: ['admin', 'employee'],
      permission: 'purchases.view',
      module: 'purchases'
    },
    {
      name: 'Purchase Invoices',
      icon: DocumentTextIcon,
      path: '/dashboard/purchase-invoices',
      roles: ['admin', 'employee'],
      permission: 'purchases.view',
      module: 'purchases'
    },
    {
      name: 'Warehouse',
      icon: BuildingStorefrontIcon,
      path: '/dashboard/warehouse',
      roles: ['admin', 'employee'],
      permission: 'warehouse.view',
      module: 'warehouse'
    },
    {
      name: 'Reports',
      icon: DocumentTextIcon,
      path: '/dashboard/reports',
      roles: ['admin', 'employee'],
      permission: 'reports.view',
      module: 'reports'
    },
    {
      name: 'My Referrals',
      icon: UsersIcon,
      path: '/dashboard/my-referrals',
      roles: ['employee']
    },
    {
      name: 'Settings',
      icon: Cog6ToothIcon,
      path: '/dashboard/settings',
      roles: ['admin'],
      permission: 'settings.view',
      module: 'settings'
    },
    {
      name: 'Outlets',
      icon: BuildingStorefrontIcon,
      path: '/dashboard/outlets',
      roles: ['admin'],
      permission: 'settings.view',
      module: 'settings'
    }
  ];

  const notificationItems = [
    {
      name: 'Transactions',
      icon: DocumentTextIcon,
      path: '/dashboard/transactions',
      roles: ['admin', 'employee'],
      permission: 'transactions.view',
      module: 'transactions'
    },
    {
      name: 'Messages',
      icon: EnvelopeIcon,
      path: '/dashboard/messages',
      roles: ['admin', 'employee'],
      permission: 'notifications.view',
      module: 'notifications'
    }
  ];

  const canAccessMenuItem = (item) => {
    // Check role first
    if (!item.roles.includes(user?.role)) return false;
    
    // Super Admin and Admin roles have full access
    if (user?.role === 'admin') {
      // Check if user has a specific role assigned
      const userRole = user?.role_name || user?.role;
      if (userRole === 'super_admin') return true;
      // Regular admin - check if they have settings.manage_roles permission (only super admin should have this)
      if (item.permission === 'settings.manage_roles') {
        return hasPermission('settings.manage_roles');
      }
      // For other items, admin has access unless it's a super-admin-only feature
      return true;
    }
    
    // If no access controls are declared, allow access.
    if (!item.permission && !item.module) return true;

    // When a permission is declared, require it explicitly.
    // This prevents module-level access from re-enabling a revoked permission.
    let hasRequiredPermission = true;
    if (item.permission) {
      hasRequiredPermission = hasPermission(item.permission)
        || (item.fallbackPermission && hasPermission(item.fallbackPermission));
    }

    // Module access still applies as an additional gate when configured.
    let hasRequiredModule = true;
    if (item.module) {
      hasRequiredModule = hasModule(item.module);
    }

    return hasRequiredPermission && hasRequiredModule;
  };

  const filteredMenuItems = menuItems.filter(canAccessMenuItem);

  const filteredNotificationItems = notificationItems.filter(canAccessMenuItem);

  const navSearchResults = useMemo(() => {
    const q = navSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return [...filteredMenuItems, ...filteredNotificationItems].filter((item) =>
      item.name.toLowerCase().includes(q)
    );
  }, [navSearchQuery, filteredMenuItems, filteredNotificationItems]);

  const goToSearchResult = (path) => {
    navigate(path);
    setNavSearchQuery('');
    setNavSearchOpen(false);
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' && !location.pathname.includes('/dashboard/');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Dark Blue Sidebar - Viverra Style */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed xl:static inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-blue-900 to-blue-800 transform transition-transform duration-300 ease-in-out xl:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-blue-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">V</span>
              </div>
              <span className="text-white text-xl font-semibold">Viverra</span>
            </div>
          </div>

          {/* Menu Section */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="px-4 mb-4">
              <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-wider mb-2">Menu</h3>
            </div>
            <nav className="px-2 space-y-1">
              {filteredMenuItems.slice(0, 3).map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      if (window.innerWidth < 1280) {
                        setSidebarOpen(false);
                      }
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition ${
                      active
                        ? 'bg-blue-700 text-white'
                        : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </button>
                );
              })}
            </nav>

            {/* Management Section */}
            <div className="px-4 mt-6 mb-4">
              <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-wider mb-2">Management</h3>
            </div>
            <nav className="px-2 space-y-1">
              {filteredMenuItems.slice(3).map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      if (window.innerWidth < 1280) {
                        setSidebarOpen(false);
                      }
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition ${
                      active
                        ? 'bg-blue-700 text-white'
                        : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </button>
                );
              })}
            </nav>

            {/* Notifications Section */}
            {filteredNotificationItems.length > 0 && (
              <>
                <div className="px-4 mt-6 mb-4">
                  <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-wider mb-2">Notifications</h3>
                </div>
                <nav className="px-2 space-y-1">
                  {filteredNotificationItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          if (window.innerWidth < 1280) {
                            setSidebarOpen(false);
                          }
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition ${
                          active
                            ? 'bg-blue-700 text-white'
                            : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </div>

          {/* User Profile at Bottom */}
          <div className="p-4 border-t border-blue-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-blue-300 text-xs truncate capitalize">
                  {user?.role === 'admin' ? 'Admin Manager' : user?.role === 'employee' ? 'Retail Manager' : user?.role}
                </p>
              </div>
            </div>
            {/* Logout - the top nav bar's Logout button sits behind the mobile sidebar overlay
                and isn't reachable while the drawer is open, so it's repeated here directly. */}
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-200 hover:text-white hover:bg-blue-800 rounded-lg transition"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              Log Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      {/* min-w-0 overrides the flex item's default min-width:auto - without it, a deeply
          nested descendant's intrinsic content width (e.g. the top-bar search input) can force
          this whole column wider than the sidebar row it lives in, and since body has
          overflow-x:hidden globally, the excess silently gets clipped instead of scrolling -
          every page under this layout loses content off the right edge on narrow screens. */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 xl:hidden"
                >
                  {sidebarOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
                <div className="ml-3 sm:ml-4 xl:ml-0 relative flex-1 min-w-0 max-w-xs sm:max-w-sm" ref={navSearchRef}>
                  <input
                    type="text"
                    placeholder="Search sections (Orders, Settings...)"
                    value={navSearchQuery}
                    onChange={(e) => {
                      setNavSearchQuery(e.target.value);
                      setNavSearchOpen(true);
                    }}
                    onFocus={() => { if (navSearchQuery.trim()) setNavSearchOpen(true); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && navSearchResults.length > 0) {
                        goToSearchResult(navSearchResults[0].path);
                      } else if (e.key === 'Escape') {
                        setNavSearchOpen(false);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {navSearchOpen && navSearchQuery.trim() && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
                      {navSearchResults.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500">No matching section. Try "Orders", "Settings", "Customers"...</p>
                      ) : (
                        navSearchResults.map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => goToSearchResult(item.path)}
                              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              {item.name}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* Dark Mode Toggle (placeholder) */}
                <button className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                </button>

                {/* Notifications */}
                <button
                  ref={notifBtnRef}
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full"
                >
                  <BellIcon className="h-6 w-6" />
                  {alertCount > 0 ? (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold leading-none">
                      {alertCount > 9 ? '9+' : alertCount}
                    </span>
                  ) : (
                    <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-gray-300 ring-2 ring-white"></span>
                  )}
                </button>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Log Out</span>
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 sm:p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Notifications Panel */}
      {notificationsOpen && (
        <div ref={notifPanelRef} className="fixed right-4 top-20 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 flex flex-col" style={{maxHeight: '28rem'}}>
          <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
            {alertCount > 0 && <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">{alertCount} new</span>}
          </div>
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2 overflow-x-auto">
            {Object.keys(sectionCounts).length === 0 ? (
              <span className="text-xs text-gray-400">No active alert groups</span>
            ) : (
              Object.entries(sectionCounts).map(([section, count]) => (
                <span key={section} className="text-xs font-medium bg-white border border-gray-200 text-gray-700 px-2 py-1 rounded-full whitespace-nowrap">
                  {section}: {count}
                </span>
              ))
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {groupedAlerts.length === 0 ? (
              <div className="p-6 text-center">
                <BellIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No alerts right now</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {groupedAlerts.map((group) => {
                  const colorMap = {
                    error: { bar: 'bg-red-500', bg: 'hover:bg-red-50', dot: 'bg-red-500', text: 'text-red-700' },
                    warning: { bar: 'bg-yellow-400', bg: 'hover:bg-yellow-50', dot: 'bg-yellow-400', text: 'text-yellow-700' },
                    success: { bar: 'bg-green-500', bg: 'hover:bg-green-50', dot: 'bg-green-500', text: 'text-green-700' },
                    info: { bar: 'bg-blue-500', bg: 'hover:bg-blue-50', dot: 'bg-blue-400', text: 'text-blue-700' },
                  };
                  const c = colorMap[group.type] || colorMap.info;
                  const priority = group.priority === 'high';
                  const isExpanded = !!expandedGroups[group.key];
                  return (
                    <li key={group.key} className={`${priority ? 'bg-red-50' : ''}`}>
                      <div className={`flex items-start gap-3 px-4 py-3 ${c.bg}`}>
                        <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${c.dot}`}></span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-semibold ${c.text} truncate`}>{group.title}</p>
                            <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{group.count}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{getGroupSummary(group)}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleGroup(group.key)}
                            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-white"
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                          {group.link && (
                            <button
                              onClick={() => { navigate(group.link); setNotificationsOpen(false); }}
                              className="text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-white"
                            >
                              Open
                            </button>
                          )}
                          {priority && <span className="flex-shrink-0 text-xs bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded">!</span>}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-6 pb-3 pt-1 bg-white">
                          <ul className="space-y-1">
                            {group.items.slice(0, 8).map((item, idx) => (
                              <li key={`${group.key}_item_${idx}`} className="text-xs text-gray-600 leading-5 border-l-2 border-gray-200 pl-2">
                                {item.message}
                              </li>
                            ))}
                          </ul>
                          {group.items.length > 8 && (
                            <p className="text-xs text-gray-400 mt-1">+{group.items.length - 8} more</p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="p-3 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={() => { navigate('/dashboard'); setNotificationsOpen(false); }}
              className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium text-center"
            >
              View Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-30 xl:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
